import React, { useState, useEffect } from 'react';
import { Mail, Lock, LogOut, Eye, EyeOff, User, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../supabase';
import { trackLogout, trackConsentGiven, trackConsentRevoked } from '../utils/analytics';
import { PersonalProfile } from './PersonalProfile';
import SectionHeader from './SectionHeader';

// Optional exit-survey reasons. Kept neutral and short; answering is never required.
const DELETE_REASONS = [
  "Not using it enough",
  "Didn't find it useful",
  "Privacy concerns",
  "Too many emails",
  "Just trying it out",
];

export function AccountSettings({ onBack, onImageConsentChange, onContinueToChat }) {
  const { user, signOut } = useAuth();
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteFeedback, setDeleteFeedback] = useState('');

  // Opens the user's mail client to complete an account-deletion request. Any
  // feedback the user optionally gave rides in the email body — we store no new
  // data here, and this never blocks or gates the deletion (GDPR right to erasure).
  const submitDeletionEmail = () => {
    const parts = [];
    if (deleteReason) parts.push(`Reason: ${deleteReason}`);
    if (deleteFeedback.trim()) parts.push(deleteFeedback.trim());
    const extra = parts.length ? `\n\n---\n${parts.join('\n')}` : '';
    const subject = encodeURIComponent('Delete my account');
    const body = encodeURIComponent(`Please delete my Son:mi account and all associated data.${extra}`);
    window.location.href = `mailto:info@sonmi.io?subject=${subject}&body=${body}`;
    setShowDeleteDialog(false);
  };
  const [showPersonalProfile, setShowPersonalProfile] = useState(false);

  // Image consent toggle
  const [imageConsent, setImageConsent] = useState(false);
  const [consentLoading, setConsentLoading] = useState(true);

  // Load current user email
  useEffect(() => {
    if (user?.email) {
      setCurrentEmail(user.email);
    }
  }, [user]);

  // Load image consent status
  useEffect(() => {
    if (!user?.id) return;
    const loadConsent = async () => {
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('image_consent, image_consent_at')
          .eq('user_id', user.id)
          .maybeSingle();
        // Require both flag and timestamp — without a grant timestamp we
        // can't prove the user explicitly opted in (mirrors ChatComponent).
        setImageConsent(data?.image_consent === true && !!data?.image_consent_at);
      } catch (err) {
        console.error('Error loading image consent:', err);
      } finally {
        setConsentLoading(false);
      }
    };
    loadConsent();
  }, [user?.id]);

  const handleToggleImageConsent = async () => {
    const newValue = !imageConsent;
    setImageConsent(newValue);
    if (onImageConsentChange) onImageConsentChange(newValue);
    try {
      await supabase.from('user_profiles').upsert(
        {
          user_id: user.id,
          image_consent: newValue,
          image_consent_at: new Date().toISOString(),
          image_consent_version: '1.0',
        },
        { onConflict: 'user_id' }
      );
      if (newValue) {
        trackConsentGiven();
      } else {
        trackConsentRevoked();
      }
    } catch (err) {
      console.error('Error updating image consent:', err);
      setImageConsent(!newValue); // revert on error
      if (onImageConsentChange) onImageConsentChange(!newValue); // revert parent too
    }
  };

  const handleEmailUpdate = async () => {
    setEmailError('');
    setEmailSuccess('');

    if (!newEmail || !emailPassword) {
      setEmailError('Please fill in all fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      // Import supabase
      const { supabase } = await import('../supabase');
      
      // First, verify the password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password: emailPassword
      });

      if (signInError) {
        setEmailError('Incorrect password');
        return;
      }

      // Update email
      const { error: updateError } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (updateError) {
        setEmailError(updateError.message);
        return;
      }

      setCurrentEmail(newEmail);
      setNewEmail('');
      setEmailPassword('');
      setIsEditingEmail(false);
      setEmailSuccess('Email updated successfully! Please check your new email to confirm.');
      
      // Clear success message after 5 seconds
      setTimeout(() => setEmailSuccess(''), 5000);
    } catch (error) {
      setEmailError('Failed to update email. Please try again.');
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all password fields');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      // Import supabase
      const { supabase } = await import('../supabase');
      
      // Verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password: currentPassword
      });

      if (signInError) {
        setPasswordError('Current password is incorrect');
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        setPasswordError(updateError.message);
        return;
      }

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangingPassword(false);
      setPasswordSuccess('Password changed successfully!');
      
      // Clear success message after 5 seconds
      setTimeout(() => setPasswordSuccess(''), 5000);
    } catch (error) {
      setPasswordError('Failed to change password. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      // Track logout event
      trackLogout();

      // Close dialog immediately so the UI doesn't hang
      setShowLogoutDialog(false);

      // Sign out — onAuthStateChange will re-init an anonymous session,
      // which triggers isAnonymous=true in App.js and shows the welcome screen
      await signOut();

      // Close the settings panel
      onBack();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // If showing personal profile, render that instead
  if (showPersonalProfile) {
    return <PersonalProfile onBack={() => setShowPersonalProfile(false)} userId={user?.id} onContinueToChat={onContinueToChat} />;
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="px-4 pt-3 pb-2 bg-white flex-shrink-0 border-b border-[#EAE0D4]">
        <SectionHeader title="Account Settings" subtitle="Manage your security and preferences" onBack={onBack} />
      </div>

      {/* Scrollable Content — pt-3 so the avatar's ring isn't clipped at the top */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 space-y-4">
        
        {/* Profile Overview */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 pb-2">
            <div className="w-11 h-11 flex-shrink-0 bg-gradient-to-br from-[#F3D5B5] to-[#F3D5B5]/60 rounded-full flex items-center justify-center ring-4 ring-[#F2EAE1] shadow-sm">
              <User className="h-5 w-5 text-[#2A324B]" />
            </div>
            <div>
              <p className="text-[#2A324B] font-medium">Welcome back</p>
              <p className="text-sm text-[#2A324B]/60 break-all">{currentEmail}</p>
            </div>
          </div>
          <Separator className="bg-[#2A324B]/10" />
        </div>

        {/* Personal Profile Section */}
        <button
          onClick={() => setShowPersonalProfile(true)}
          className="w-full text-left"
        >
          <div className="flex items-center justify-between p-3 bg-[#F2EAE1]/30 hover:bg-[#F2EAE1]/50 rounded-lg transition-colors group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#F3D5B5] to-[#F3D5B5]/60 rounded-lg flex items-center justify-center shadow-sm">
                <User className="h-5 w-5 text-[#2A324B]" />
              </div>
              <div>
                <h3 className="text-[#2A324B] font-medium">Personal Profile</h3>
                <p className="text-xs text-[#2A324B]/60">Your skincare identity & preferences</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-[#2A324B]/40 group-hover:text-[#2A324B]/60 group-hover:translate-x-0.5 transition-all" />
          </div>
        </button>

        <Separator className="bg-[#2A324B]/10" />

        {/* Email Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#F3D5B5] to-[#F3D5B5]/60 rounded-lg flex items-center justify-center shadow-sm">
              <Mail className="h-5 w-5 text-[#2A324B]" />
            </div>
            <div>
              <h3 className="text-[#2A324B] font-medium">Email Address</h3>
              <p className="text-xs text-[#2A324B]/60">Update your email for account recovery</p>
            </div>
          </div>

          {emailSuccess && (
            <div className="pl-0 sm:pl-[52px] text-sm text-green-600 bg-green-50 p-3 rounded-md">
              {emailSuccess}
            </div>
          )}

          {!isEditingEmail ? (
            <div className="space-y-2 pl-0 sm:pl-[52px]">
              <div className="space-y-1">
                <p className="text-xs text-[#2A324B]/60">Current Email</p>
                <p className="text-[#2A324B] break-all">{currentEmail}</p>
              </div>
              <Button
                variant="outline"
                className="border-[#2A324B]/20 text-[#2A324B] hover:bg-[#F3D5B5]/10 hover:border-[#F3D5B5]"
                onClick={() => setIsEditingEmail(true)}
              >
                Change Email
              </Button>
            </div>
          ) : (
            <div className="space-y-3 pl-0 sm:pl-[52px]">
              {emailError && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  {emailError}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="newEmail" className="text-sm text-[#2A324B]/80">
                  New Email Address
                </Label>
                <Input
                  id="newEmail"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="border-[#2A324B]/20 focus:border-[#F3D5B5] focus:ring-[#F3D5B5]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emailPassword" className="text-sm text-[#2A324B]/80">
                  Confirm with Password
                </Label>
                <Input
                  id="emailPassword"
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="border-[#2A324B]/20 focus:border-[#F3D5B5] focus:ring-[#F3D5B5]"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  className="bg-[#F3D5B5] text-[#2A324B] hover:bg-[#F3D5B5]/90"
                  onClick={handleEmailUpdate}
                >
                  Update Email
                </Button>
                <Button
                  variant="ghost"
                  className="text-[#2A324B]/70 hover:bg-[#2A324B]/5"
                  onClick={() => {
                    setIsEditingEmail(false);
                    setNewEmail('');
                    setEmailPassword('');
                    setEmailError('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        <Separator className="bg-[#2A324B]/10" />

        {/* Password Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#F3D5B5] to-[#F3D5B5]/60 rounded-lg flex items-center justify-center shadow-sm">
              <Lock className="h-5 w-5 text-[#2A324B]" />
            </div>
            <div>
              <h3 className="text-[#2A324B] font-medium">Password</h3>
              <p className="text-xs text-[#2A324B]/60">Ensure your account is secure</p>
            </div>
          </div>

          {passwordSuccess && (
            <div className="pl-0 sm:pl-[52px] text-sm text-green-600 bg-green-50 p-3 rounded-md">
              {passwordSuccess}
            </div>
          )}

          {!isChangingPassword ? (
            <div className="space-y-2 pl-0 sm:pl-[52px]">
              <div className="space-y-1">
                <p className="text-xs text-[#2A324B]/60">Password</p>
                <p className="text-[#2A324B] tracking-wider">••••••••••••</p>
              </div>
              <Button
                variant="outline"
                className="border-[#2A324B]/20 text-[#2A324B] hover:bg-[#F3D5B5]/10 hover:border-[#F3D5B5]"
                onClick={() => setIsChangingPassword(true)}
              >
                Change Password
              </Button>
            </div>
          ) : (
            <div className="space-y-3 pl-0 sm:pl-[52px]">
              {passwordError && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  {passwordError}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className="text-sm text-[#2A324B]/80">
                  Current Password
                </Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="pr-10 border-[#2A324B]/20 focus:border-[#F3D5B5] focus:ring-[#F3D5B5]"
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4 text-[#2A324B]/40" />
                    ) : (
                      <Eye className="h-4 w-4 text-[#2A324B]/40" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm text-[#2A324B]/80">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="pr-10 border-[#2A324B]/20 focus:border-[#F3D5B5] focus:ring-[#F3D5B5]"
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-[#2A324B]/40" />
                    ) : (
                      <Eye className="h-4 w-4 text-[#2A324B]/40" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-[#2A324B]/50">
                  Must be at least 8 characters
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm text-[#2A324B]/80">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    className="pr-10 border-[#2A324B]/20 focus:border-[#F3D5B5] focus:ring-[#F3D5B5]"
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-[#2A324B]/40" />
                    ) : (
                      <Eye className="h-4 w-4 text-[#2A324B]/40" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  className="bg-[#F3D5B5] text-[#2A324B] hover:bg-[#F3D5B5]/90"
                  onClick={handlePasswordChange}
                >
                  Update Password
                </Button>
                <Button
                  variant="ghost"
                  className="text-[#2A324B]/70 hover:bg-[#2A324B]/5"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        <Separator className="bg-[#2A324B]/10" />

        {/* Photo Analysis Consent */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#F3D5B5] to-[#F3D5B5]/60 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-lg">📸</span>
            </div>
            <div className="flex-1">
              <h3 className="text-[#2A324B] font-medium">Photo Analysis</h3>
              <p className="text-xs text-[#2A324B]/60">Allow Son:mi to analyze your photos for recommendations</p>
            </div>
          </div>
          <div className="pl-0 sm:pl-[52px] space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#2A324B]/80">Photo analysis permission</span>
              <button
                onClick={handleToggleImageConsent}
                disabled={consentLoading}
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none"
                style={{ background: imageConsent ? '#341C1C' : '#D1C9C0' }}
                aria-label="Toggle photo analysis consent"
              >
                <span
                  className="inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200"
                  style={{ transform: imageConsent ? 'translateX(22px)' : 'translateX(4px)' }}
                />
              </button>
            </div>
            <p className="text-xs text-[#2A324B]/45 leading-relaxed">
              When enabled, Son:mi analyzes your uploaded photos to provide shade matches and personalised recommendations. Your data also helps improve recommendations for all skin tones.
            </p>
          </div>
        </div>

        <Separator className="bg-[#2A324B]/10" />

        {/* Logout Section */}
        <div className="space-y-3 pb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#341C1C]/10 to-[#341C1C]/5 rounded-lg flex items-center justify-center border border-[#341C1C]/10 shadow-sm">
              <LogOut className="h-5 w-5 text-[#341C1C]" />
            </div>
            <div>
              <h3 className="text-[#2A324B] font-medium">Sign Out</h3>
              <p className="text-xs text-[#2A324B]/60">End your current session</p>
            </div>
          </div>
          
          <div className="pl-0 sm:pl-[52px] space-y-2">
            <Button
              variant="outline"
              className="border-[#341C1C]/20 text-[#341C1C] hover:bg-[#341C1C]/5 hover:border-[#341C1C]/30"
              onClick={() => setShowLogoutDialog(true)}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log Out
            </Button>
            <p className="text-xs text-[#2A324B]/45 leading-relaxed">
              Questions about your data?{' '}
              <a href="mailto:info@sonmi.io" className="underline text-[#2A324B]/60 hover:text-[#341C1C] transition-colors">
                Email us
              </a>.
            </p>
            <button
              type="button"
              onClick={() => setShowDeleteDialog(true)}
              className="text-xs text-[#2A324B]/60 underline hover:text-[#341C1C] transition-colors text-left"
            >
              Delete my account
            </button>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="bg-white border-[#341C1C]/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#2A324B]">
              Log Out
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#2A324B]/70">
              Are you sure you want to log out? You'll need to sign in again to access your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#2A324B]/20 text-[#2A324B]"
              onClick={() => setShowLogoutDialog(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogout}
              className="bg-[#341C1C] text-white hover:bg-[#341C1C]/90"
            >
              Log Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete-account: optional exit feedback, then an email to complete the
          request. Feedback is optional and never blocks deletion (GDPR Art. 17). */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white border-[#341C1C]/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#2A324B]">Before you go</AlertDialogTitle>
            <AlertDialogDescription className="text-[#2A324B]/70">
              Please share why you're leaving so we can improve.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 pt-1">
            <select
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              className="w-full text-sm rounded-lg border border-[#341C1C]/15 p-2.5 text-[#2A324B] bg-white focus:outline-none focus:ring-1 focus:ring-[#341C1C]/30"
            >
              <option value="">Select a reason</option>
              {DELETE_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <textarea
              value={deleteFeedback}
              onChange={(e) => setDeleteFeedback(e.target.value)}
              placeholder="Anything else?"
              rows={3}
              className="w-full text-sm rounded-lg border border-[#341C1C]/15 p-2.5 text-[#2A324B] placeholder:text-[#2A324B]/40 focus:outline-none focus:ring-1 focus:ring-[#341C1C]/30 resize-none"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#2A324B]/20 text-[#2A324B]"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={submitDeletionEmail}
              className="bg-[#341C1C] text-white hover:bg-[#341C1C]/90"
            >
              Proceed to delete my account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

